import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Brain,
  CheckCircle2,
  FileText,
  GitBranch,
  Layers3,
  Play,
  SearchCheck,
  Sparkles,
} from "lucide-react";

const benefits = [
  {
    icon: Layers3,
    title: "Ground runs in product memory and project context",
  },
  {
    icon: SearchCheck,
    title: "Evaluate ambiguity, gaps, and readiness before handoff",
  },
  {
    icon: GitBranch,
    title: "Refine with versions, traceability, and export-ready output",
  },
];

const workflowPreview = [
  { label: "Input", value: "Rough idea or PRD draft" },
  { label: "Context", value: "Product memory + project rules" },
  { label: "Review", value: "Readiness, gaps, questions" },
  { label: "Output", value: "Requirements, versions, exports" },
];

export function HeroSection() {
  return (
    <section className="relative min-h-[86vh] flex items-center overflow-hidden">
      <div className="absolute top-[-20%] left-1/2 h-[520px] w-[760px] -translate-x-1/2 rounded-full bg-primary/[0.10] blur-[150px]" />
      <div className="absolute bottom-[-18%] right-[8%] h-[360px] w-[520px] rounded-full bg-accent/[0.06] blur-[130px]" />
      <div className="absolute inset-0 opacity-[0.03] mix-blend-overlay" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
      }} />
      <div
        className="absolute inset-0 opacity-[0.025]"
        style={{
          backgroundImage: `linear-gradient(hsl(var(--primary) / 0.5) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary) / 0.5) 1px, transparent 1px)`,
          backgroundSize: "72px 72px",
        }}
      />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

      <div className="container relative z-10 px-4 md:px-6 py-16 md:py-24">
        <div className="grid gap-12 lg:grid-cols-[1.02fr_0.98fr] lg:items-center">
          <div className="max-w-3xl">
            <Link
              to="/beta"
              className="hero-stagger-1 group inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/[0.06] px-3.5 py-1.5 text-sm backdrop-blur-md transition-all duration-300 hover:border-primary/40 hover:bg-primary/[0.10]"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-reqqy-success opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-reqqy-success" />
              </span>
              <span className="text-muted-foreground transition-colors group-hover:text-foreground">
                Requirements intelligence for product teams
              </span>
              <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/60 transition-transform group-hover:translate-x-0.5" />
            </Link>

            <h1 className="hero-stagger-2 mt-7 max-w-4xl text-4xl font-bold leading-[1.04] tracking-[-0.035em] sm:text-5xl md:text-6xl lg:text-7xl">
              Turn rough ideas and PRDs into clearer requirements.
            </h1>

            <p className="hero-stagger-3 mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg lg:text-xl">
              Reqqy helps teams capture product context, generate or review requirements, evaluate readiness,
              refine with version history, and export into delivery-ready formats before engineering time is spent.
            </p>

            <div className="hero-stagger-4 mt-8 flex flex-col gap-3 sm:flex-row">
              <Button
                asChild
                size="lg"
                className="relative group h-11 px-7 text-sm font-semibold gradient-primary text-primary-foreground overflow-hidden transition-all duration-300 hover:shadow-[0_0_30px_-8px_hsl(var(--primary)/0.5)]"
              >
                <Link to="/auth?mode=signup">
                  <span className="relative z-10 flex items-center gap-2">
                    <Sparkles className="h-4 w-4" />
                    Start free
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                  <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/10 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="h-11 px-7 text-sm border-border/60 hover:border-primary/40">
                <a href="https://youtu.be/TJ9xio6_pGw?si=AAfyEB3YnFInhQat" target="_blank" rel="noreferrer">
                  <Play className="mr-2 h-4 w-4" />
                  Watch demo
                </a>
              </Button>
            </div>

            <p className="hero-stagger-5 mt-4 text-xs tracking-wide text-muted-foreground/70">
              No credit card required · Built for idea-to-requirements and PRD review workflows
            </p>

            <div className="hero-stagger-6 mt-10 grid gap-3 sm:grid-cols-3">
              {benefits.map((benefit) => (
                <div
                  key={benefit.title}
                  className="group flex items-start gap-2.5 rounded-xl border border-border/30 bg-card/[0.45] p-3.5 text-left backdrop-blur-md transition-all duration-300 hover:border-primary/20 hover:bg-card/[0.65]"
                >
                  <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10">
                    <benefit.icon className="h-3.5 w-3.5 text-primary" />
                  </div>
                  <p className="text-xs font-medium leading-snug text-foreground/90">{benefit.title}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="hero-stagger-6 relative mx-auto w-full max-w-xl lg:max-w-none">
            <div className="absolute -inset-4 rounded-[2rem] bg-primary/[0.06] blur-3xl" />
            <div className="relative overflow-hidden rounded-2xl border border-border/40 bg-card/70 shadow-2xl backdrop-blur-xl">
              <div className="flex items-center justify-between border-b border-border/40 px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="h-2.5 w-2.5 rounded-full bg-destructive/70" />
                  <div className="h-2.5 w-2.5 rounded-full bg-yellow-500/70" />
                  <div className="h-2.5 w-2.5 rounded-full bg-reqqy-success/80" />
                </div>
                <div className="rounded-full border border-primary/20 bg-primary/10 px-2.5 py-1 text-[11px] font-medium text-primary">
                  Run succeeded
                </div>
              </div>

              <div className="space-y-4 p-5">
                <div>
                  <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                    <Brain className="h-3.5 w-3.5" />
                    Smart Rider Assignment
                  </div>
                  <h3 className="text-xl font-semibold tracking-tight">Requirements readiness: 72%</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Generated from product memory, project context, and run input.
                  </p>
                </div>

                <div className="grid gap-2 sm:grid-cols-2">
                  {workflowPreview.map((item) => (
                    <div key={item.label} className="rounded-xl border border-border/40 bg-background/50 p-3">
                      <div className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">{item.label}</div>
                      <div className="mt-1 text-sm font-medium leading-snug">{item.value}</div>
                    </div>
                  ))}
                </div>

                <div className="rounded-xl border border-border/40 bg-background/55 p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm font-semibold">
                      <FileText className="h-4 w-4 text-primary" />
                      Generated requirements
                    </div>
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">v2</span>
                  </div>
                  <div className="space-y-2">
                    {[
                      "Recommend best available rider for each order",
                      "Explain recommendation factors to dispatcher",
                      "Handle unavailable, overloaded, or stale riders",
                    ].map((line) => (
                      <div key={line} className="flex items-start gap-2 rounded-lg bg-card/60 p-2.5 text-sm text-muted-foreground">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-reqqy-success" />
                        <span>{line}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background to-transparent" />
    </section>
  );
}
